import React from 'react';
import { Select } from '@amzn/awsui-components-react-v3/polaris';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { Locale, localeLabel } from '../../utils/locales';

/**
 * Dropdown of every locale, labelled "name (code)". Used by the base rates and TER group edit
 * tables. There is no Any: a null value shows a placeholder until a locale is chosen.
 */

interface LocaleOption {
  label: string;
  value: string;
}

interface Options {
  options: LocaleOption[];
  byCode: Map<string, LocaleOption>;
}

// A table renders one dropdown per cell, so the options are built once per locale list.
const optionsByLocales = new WeakMap<Locale[], Options>();

const optionsFor = (locales: Locale[]): Options => {
  let built = optionsByLocales.get(locales);
  if (!built) {
    const options = locales.map(locale => ({
      label: localeLabel(locale.code, locale),
      value: locale.code,
    }));
    built = { options, byCode: new Map(options.map(option => [option.value, option])) };
    optionsByLocales.set(locales, built);
  }
  return built;
};

interface LocaleSelectProps {
  id: string;
  // Null until a locale is chosen.
  value: string | null;
  locales: Locale[];
  invalid?: boolean;
  disabled?: boolean;
  onChange: (code: string) => void;
}

const LocaleSelect = ({
  id,
  value,
  locales,
  invalid,
  disabled,
  onChange,
}: LocaleSelectProps): JSX.Element => {
  const { options, byCode } = optionsFor(locales);
  // A value missing from the list still shows its code, so callers can flag it.
  const selected = value === null ? null : byCode.get(value) || { label: value, value };
  return (
    <Select
      id={id}
      selectedOption={selected}
      placeholder="Choose a locale"
      options={options}
      filteringType="auto"
      // Tables scroll their cells, which would clip a dropdown rendered inside them.
      expandToViewport
      invalid={invalid}
      disabled={disabled}
      onChange={({ detail }): void => {
        const code = detail.selectedOption.value;
        if (code !== undefined) {
          onChange(code);
        }
      }}
    />
  );
};

export default LocaleSelect;
